import { useState, useEffect } from "react";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { surgeryService, type PostOpMedicationItem } from "../surgeryService";
import ConfirmModal from "../../../components/common/ConfirmModal";

const ROUTE_SUGGESTIONS = [
  "VO", "IV", "IM", "SC", "Tópico", "Sublingual",
  "Inhalado", "Rectal", "Oftálmico", "Ótico", "EV",
];

interface PostOpPrescriptionModalProps {
  procedureId: number;
  open: boolean;
  generating: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const emptyItem = (): PostOpMedicationItem => ({
  medication_name: "",
  dosage: "",
  frequency: "",
  duration: "",
  route: "",
  notes: "",
});

const PostOpPrescriptionModal: React.FC<PostOpPrescriptionModalProps> = ({
  procedureId,
  open,
  generating,
  onClose,
  onSaved,
}) => {
  const [items, setItems] = useState<PostOpMedicationItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);
  const [pendingClose, setPendingClose] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError("");
    setDirty(false);

    surgeryService
      .getPostOpPrescription(procedureId)
      .then((prescription) => {
        if (prescription?.items?.length) {
          setItems(prescription.items);
        } else {
          setItems([emptyItem()]);
        }
      })
      .catch(() => {
        setItems([emptyItem()]);
      });
  }, [open, procedureId]);

  const handleChange = (index: number, field: keyof PostOpMedicationItem, value: string) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const handleAdd = () => {
    setItems((prev) => [...prev, emptyItem()]);
    setDirty(true);
  };

  const requestRemove = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const cancelRemove = () => {
    setPendingRemoveIndex(null);
  };

  const handleRemove = () => {
    if (pendingRemoveIndex == null) return;
    setItems((prev) => prev.filter((_, i) => i !== pendingRemoveIndex));
    setDirty(true);
    setPendingRemoveIndex(null);
  };

  const handleSave = async () => {
    const cleaned = items.filter((i) => i.medication_name.trim());
    if (cleaned.length === 0) {
      setError("Debe agregar al menos un medicamento.");
      return;
    }

    for (const item of cleaned) {
      if (!item.medication_name || !item.dosage || !item.frequency || !item.duration || !item.route) {
        setError("Todos los campos obligatorios (medicamento, dosis, frecuencia, duración, vía) deben completarse en cada fila.");
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      await surgeryService.savePostOpPrescription(procedureId, cleaned);
      setDirty(false);
      onSaved();
    } catch {
      setError("Error al guardar la receta. Intente nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (dirty) {
      setPendingClose(true);
    } else {
      onClose();
    }
  };

  const cancelPendingClose = () => {
    setPendingClose(false);
  };

  const confirmPendingClose = () => {
    setPendingClose(false);
    onClose();
  };

  const handleClose = () => {
    requestClose();
  };

  if (!open) return null;

  const allEmpty = items.length === 1 && !items[0].medication_name;

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">
                <FeatherIcon icon="file-text" size={18} className="me-2" />
                Receta Post-Operatoria
              </h5>
              <button type="button" className="btn-close btn-close-white" onClick={handleClose} disabled={saving || generating} />
            </div>

            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}

              {(generating) && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <p className="text-muted mb-0">Generando receta desde plantilla...</p>
                </div>
              )}

              {!generating && allEmpty && !error && (
                <div className="text-center py-4 text-muted">
                  <FeatherIcon icon="file-text" size={32} className="mb-2" />
                  <p className="mb-3">No se encontró una plantilla para este procedimiento.</p>
                  <button className="btn btn-outline-primary" onClick={handleAdd}>
                    <FeatherIcon icon="plus" size={14} className="me-1" /> Agregar Medicamento Manualmente
                  </button>
                </div>
              )}

              {!generating && !allEmpty && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted small">{items.filter((i) => i.medication_name).length} medicamento(s)</span>
                    <button className="btn btn-sm btn-outline-primary" onClick={handleAdd} disabled={saving}>
                      <FeatherIcon icon="plus" size={14} className="me-1" /> Agregar Medicamento
                    </button>
                  </div>

                  <div className="row g-3">
                    {items.map((item, idx) => (
                      <div className="col-12" key={idx}>
                        <div className="card bg-light border">
                          <div className="card-body p-3">
                            <div className="row g-2 align-items-end">
                              <div className="col-md-4">
                                <label className="form-label small mb-1">Medicamento *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.medication_name}
                                  onChange={(e) => handleChange(idx, "medication_name", e.target.value)}
                                  placeholder="Ej: Ceftriaxona"
                                  disabled={saving}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Dosis *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.dosage}
                                  onChange={(e) => handleChange(idx, "dosage", e.target.value)}
                                  placeholder="Ej: 1g"
                                  disabled={saving}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Frecuencia *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.frequency}
                                  onChange={(e) => handleChange(idx, "frequency", e.target.value)}
                                  placeholder="Ej: c/12hs"
                                  disabled={saving}
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label small mb-1">Duración *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.duration}
                                  onChange={(e) => handleChange(idx, "duration", e.target.value)}
                                  placeholder="Ej: 5 días"
                                  disabled={saving}
                                />
                              </div>
                              <div className="col-md-1">
                                <label className="form-label small mb-1">Vía *</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.route}
                                  onChange={(e) => handleChange(idx, "route", e.target.value)}
                                  placeholder="Ej: EV"
                                  list="route-suggestions"
                                  disabled={saving}
                                />
                              </div>
                              <div className="col-md-1 d-flex align-items-end">
                                <button
                                  className="btn btn-sm btn-outline-danger w-100"
                                  onClick={() => requestRemove(idx)}
                                  disabled={saving || items.length === 1}
                                  title="Eliminar medicamento"
                                >
                                  <FeatherIcon icon="trash-2" size={14} />
                                </button>
                              </div>
                              <div className="col-md-10">
                                <label className="form-label small mb-1">Notas</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={item.notes ?? ""}
                                  onChange={(e) => handleChange(idx, "notes", e.target.value)}
                                  placeholder="Ej: Administrar diluido en 100ml SF"
                                  disabled={saving}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <datalist id="route-suggestions">
                    {ROUTE_SUGGESTIONS.map((r) => (
                      <option key={r} value={r} />
                    ))}
                  </datalist>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={saving || generating}>
                Cancelar
              </button>
              {!generating && !allEmpty && (
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1" />}
                  <FeatherIcon icon="save" size={14} className="me-1" />
                  Guardar Receta
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={pendingRemoveIndex !== null}
        title="Eliminar medicamento"
        body="¿Eliminar este medicamento de la receta?"
        confirmLabel="Eliminar"
        confirmVariant="danger"
        onConfirm={handleRemove}
        onCancel={cancelRemove}
      />

      <ConfirmModal
        show={pendingClose}
        title="Descartar cambios"
        body="¿Descartar los cambios sin guardar?"
        confirmLabel="Descartar"
        confirmVariant="warning"
        onConfirm={confirmPendingClose}
        onCancel={cancelPendingClose}
      />
    </>
  );
};

export default PostOpPrescriptionModal;
